import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react";
import type { WorkCalendar } from "@/engine/calendar";
import { earliestStartFor } from "@/engine/cpm";
import { fromDay, toDay, todayISO } from "@/engine/date";
import { remainingInfo } from "@/engine/remaining";
import type { Dependency, ScheduledTask } from "@/engine/types";
import { cn } from "@/lib/utils";
import { formatDate, formatDateRange } from "@/lib/date-format";
import { useProjectStore, type BarLabel } from "@/store/project-store";
import { usePlanSurface } from "@/features/access/use-plan-access";
import { HEADER_H, type RowsViewport } from "@/features/workspace/Workspace";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { buildTimeScale, type TimeScale } from "./timescale";
import { clampPxPerDay, wheelZoomFactor } from "./zoom";
import { BarProgressLabel, fitsProgressLabel } from "./BarProgressLabel";
import { RemainingLabel } from "./RemainingLabel";

interface DragState {
  mode: "move" | "resize" | "link";
  id: string;
  startX: number;
  startY: number;
  dx: number;
  pointerX: number;
  pointerY: number;
  targetId: string | null;
  /** move: earliest calendar day allowed by project start / predecessors (constraint ignored) */
  minDay?: number;
  minReason?: string;
}

/** Effective day shift for a move drag, clamped at the earliest allowed start. */
function moveDelta(d: DragState, sched: ScheduledTask, pxPerDay: number): { days: number; clamped: boolean } {
  const raw = Math.round(d.dx / pxPerDay);
  const startDay = toDay(sched.start);
  if (d.minDay === undefined) return { days: raw, clamped: false };
  const days = Math.max(d.minDay - startDay, raw);
  return { days, clamped: days !== raw };
}

export function GanttChart({ viewport, scrollContainer }: { viewport: RowsViewport; scrollContainer: RefObject<HTMLDivElement | null> }) {
  const surface = usePlanSurface();
  const { rows, rowHeight, start, end } = viewport;
  const project = useProjectStore((s) => s.project);
  const derived = useProjectStore((s) => s.derived);
  const pxPerDay = useProjectStore((s) => s.ui.pxPerDay);
  const zoomPreset = useProjectStore((s) => s.ui.zoomPreset);
  const showBaseline = useProjectStore((s) => s.ui.showBaseline);
  const showCritical = useProjectStore((s) => s.ui.showCritical);
  const showDependencies = useProjectStore((s) => s.ui.showDependencies);
  const showProgressLine = useProjectStore((s) => s.ui.showProgressLine);
  const showFloat = useProjectStore((s) => s.ui.showFloat);
  const highlightDrivingPath = useProjectStore((s) => s.ui.highlightDrivingPath);
  const barLabel = useProjectStore((s) => s.ui.barLabel);
  const showProgressLabel = useProjectStore((s) => s.ui.showProgressLabel);
  const showRemainingLabel = useProjectStore((s) => s.ui.showRemainingLabel);
  const selection = useProjectStore((s) => s.selection);
  const focusId = useProjectStore((s) => s.focusId);
  const scrollTo = useProjectStore((s) => s.scrollTo);
  const fitRequest = useProjectStore((s) => s.fitRequest);
  const { schedule, calendar, baseline } = derived;

  // driving path of the focused task: the chain of predecessors that actually set its start,
  // plus the successors it in turn drives.
  const drivingPath = useMemo(() => {
    if (!highlightDrivingPath || !focusId || !schedule.tasks.has(focusId)) return null;
    const deps = new Set<string>();
    const tasks = new Set<string>([focusId]);
    // backward
    let cur = schedule.tasks.get(focusId);
    while (cur && cur.driver.kind === "predecessor" && !deps.has(cur.driver.dependencyId)) {
      deps.add(cur.driver.dependencyId);
      tasks.add(cur.driver.predecessorId);
      cur = schedule.tasks.get(cur.driver.predecessorId);
    }
    // forward
    const queue = [focusId];
    while (queue.length) {
      const id = queue.shift()!;
      for (const t of schedule.tasks.values()) {
        if (t.driver.kind === "predecessor" && t.driver.predecessorId === id && !tasks.has(t.id)) {
          deps.add(t.driver.dependencyId);
          tasks.add(t.id);
          queue.push(t.id);
        }
      }
    }
    return { deps, tasks };
  }, [highlightDrivingPath, focusId, schedule]);

  const rangeBounds = useMemo(() => {
    let min = project.start;
    let max = schedule.projectFinish;
    for (const t of schedule.tasks.values()) {
      if (t.start < min) min = t.start;
      if (t.finish > max) max = t.finish;
    }
    for (const task of project.tasks) {
      if (task.deadline && task.deadline < min) min = task.deadline;
      if (task.deadline && task.deadline > max) max = task.deadline;
    }
    if (baseline) {
      for (const b of Object.values(baseline.tasks)) {
        if (b.start < min) min = b.start;
        if (b.finish > max) max = b.finish;
      }
    }
    const today = todayISO();
    if (today < min) min = today;
    if (today > max) max = today;
    if (project.statusDate < min) min = project.statusDate;
    if (project.statusDate > max) max = project.statusDate;
    return { min, max };
  }, [project.start, project.statusDate, project.tasks, schedule, baseline]);

  const scale = useMemo(
    () => buildTimeScale(pxPerDay, rangeBounds.min, rangeBounds.max, zoomPreset, viewport.viewportWidth),
    [pxPerDay, rangeBounds, zoomPreset, viewport.viewportWidth],
  );

  const pendingAnchor = useRef<{ day: number; offsetX: number } | null>(null);
  const lastScrollSeq = useRef(0);

  // fit project / selection into viewport width
  const lastFitSeq = useRef(0);
  useLayoutEffect(() => {
    if (!fitRequest || fitRequest.seq === lastFitSeq.current) return;
    lastFitSeq.current = fitRequest.seq;
    const el = scrollContainer.current;
    if (!el || el.clientWidth <= 0) return;

    let minISO = schedule.projectStart;
    let maxISO = schedule.projectFinish;
    if (fitRequest.mode === "selection" && selection.length) {
      minISO = schedule.projectFinish;
      maxISO = schedule.projectStart;
      for (const id of selection) {
        const t = schedule.tasks.get(id);
        if (!t) continue;
        if (t.start < minISO) minISO = t.start;
        if (t.finish > maxISO) maxISO = t.finish;
      }
    }
    const span = Math.max(1, toDay(maxISO) - toDay(minISO) + 1);
    const pad = Math.max(2, span * 0.06);
    const nextPx = clampPxPerDay(el.clientWidth / (span + pad * 2));
    const anchorDay = toDay(minISO) - pad;
    const cur = useProjectStore.getState().ui.pxPerDay;
    if (Math.abs(nextPx - cur) < 0.05) {
      el.scrollLeft = Math.max(0, (anchorDay - scale.startDay) * scale.pxPerDay);
      return;
    }
    pendingAnchor.current = { day: anchorDay, offsetX: 0 };
    useProjectStore.getState().setUI({ pxPerDay: nextPx });
  }, [fitRequest, schedule, selection, scrollContainer, scale]);

  // scroll jumps + initial / scale-change anchoring (single layout pass so they don't fight)
  const didInit = useRef(false);
  const prevScale = useRef<TimeScale | null>(null);
  useLayoutEffect(() => {
    const el = scrollContainer.current;
    if (!el) return;

    if (scrollTo && scrollTo.seq !== lastScrollSeq.current) {
      lastScrollSeq.current = scrollTo.seq;
      didInit.current = true;
      prevScale.current = scale;
      pendingAnchor.current = null;
      const x = scale.x(toDay(scrollTo.date));
      const offset = (scrollTo.align ?? "third") === "center" ? el.clientWidth / 2 : el.clientWidth / 3;
      const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
      el.scrollLeft = Math.max(0, Math.min(x - offset, maxLeft));
      return;
    }

    const prev = prevScale.current;
    prevScale.current = scale;
    if (!didInit.current) {
      didInit.current = true;
      el.scrollLeft = Math.max(0, scale.x(toDay(project.start)) - 40);
      return;
    }
    const anchor = pendingAnchor.current;
    if (anchor) {
      el.scrollLeft = Math.max(0, (anchor.day - scale.startDay) * scale.pxPerDay - anchor.offsetX);
      pendingAnchor.current = null;
      return;
    }
    if (prev && (prev.pxPerDay !== scale.pxPerDay || prev.startDay !== scale.startDay)) {
      const leftDay = prev.startDay + el.scrollLeft / prev.pxPerDay;
      el.scrollLeft = Math.max(0, (leftDay - scale.startDay) * scale.pxPerDay);
    }
  }, [scale, project.start, scrollContainer, scrollTo]);

  // ⌘/Ctrl + wheel: continuous zoom anchored under cursor
  useEffect(() => {
    const el = scrollContainer.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const cur = useProjectStore.getState().ui.pxPerDay;
      const day = scale.startDay + (el.scrollLeft + offsetX) / scale.pxPerDay;
      const next = clampPxPerDay(cur * wheelZoomFactor(e.deltaY));
      if (Math.abs(next - cur) < 0.001) return;
      pendingAnchor.current = { day, offsetX };
      useProjectStore.getState().setUI({ pxPerDay: next });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [scrollContainer, scale]);

  const rowIndex = useMemo(() => {
    const m = new Map<string, number>();
    rows.forEach((id, i) => m.set(id, i));
    return m;
  }, [rows]);

  // ---------------- drag ----------------
  const [drag, setDrag] = useState<DragState | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const beginDrag = (e: React.PointerEvent, mode: DragState["mode"], id: string) => {
    if (mode === "link" ? !surface.editWbs : !surface.editSchedule) return;
    e.stopPropagation();
    e.preventDefault();
    const rect = bodyRef.current!.getBoundingClientRect();
    const d: DragState = {
      mode,
      id,
      startX: e.clientX,
      startY: e.clientY,
      dx: 0,
      pointerX: e.clientX - rect.left,
      pointerY: e.clientY - rect.top,
      targetId: null,
    };
    if (mode === "move") {
      const s = useProjectStore.getState();
      const es = earliestStartFor(s.project, s.derived.schedule, id, calendar);
      d.minDay = toDay(calendar.isoOf(es.ordinal));
      const drv = es.driver;
      if (drv.kind === "predecessor") {
        const predName = s.project.tasks.find((t) => t.id === drv.predecessorId)?.name ?? "선행 작업";
        const lag = drv.lag ? (drv.lag > 0 ? ` +${drv.lag}d` : ` ${drv.lag}d`) : "";
        d.minReason = `'${predName}'이(가) 끝나야 시작할 수 있어 더 앞당길 수 없음 (${drv.type}${lag})`;
      } else {
        d.minReason = "플랜 시작일보다 먼저 시작할 수 없음";
      }
    }
    dragRef.current = d;
    setDrag(d);
    try {
      bodyRef.current!.setPointerCapture(e.pointerId);
    } catch {
      /* synthetic pointer events have no capture */
    }
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const rect = bodyRef.current!.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    let targetId: string | null = null;
    if (d.mode === "link") {
      const idx = Math.floor(py / rowHeight);
      targetId = rows[idx] ?? null;
      if (targetId === d.id) targetId = null;
    }
    const next = { ...d, dx: e.clientX - d.startX, pointerX: px, pointerY: py, targetId };
    dragRef.current = next;
    setDrag(next);
  };
  const onPointerUp = () => {
    const d = dragRef.current;
    dragRef.current = null;
    setDrag(null);
    if (!d) return;
    const s = useProjectStore.getState();
    const task = s.project.tasks.find((t) => t.id === d.id);
    const sched = s.derived.schedule.tasks.get(d.id);
    if (!task || !sched) return;
    const deltaDays = Math.round(d.dx / scale.pxPerDay);
    if (d.mode === "move") {
      const { days } = moveDelta(d, sched, scale.pxPerDay);
      const newDay = toDay(sched.start) + days;
      const atEarliest = d.minDay !== undefined && newDay <= d.minDay;
      if (atEarliest) {
        // dropped at (or dragged before) the earliest possible start: the schedule decides, so drop the constraint
        if (task.constraint) s.updateTask(d.id, { constraint: undefined }, `${task.name}: 바 이동 → 제약 해제 (가능한 빨리)`);
      } else if (days !== 0) {
        const iso = calendar.isoOf(calendar.ordinalOf(newDay));
        const type = task.constraint?.type === "MSO" ? "MSO" : "SNET";
        s.updateTask(d.id, { constraint: { type, date: iso } }, `${task.name}: 바 이동 → ${formatDate(iso, "numeric")} (${type})`);
      }
    } else if (d.mode === "resize" && deltaDays !== 0) {
      const newFinishDay = toDay(sched.finish) + deltaDays;
      const dur = Math.max(1, calendar.ordinalBefore(newFinishDay) - calendar.ordinalOf(toDay(sched.start)) + 1);
      if (dur !== task.duration) s.updateTask(d.id, { duration: dur }, `${task.name}: 기간 ${task.duration}d → ${dur}d`);
    } else if (d.mode === "link" && d.targetId) {
      s.addDependency({ predecessorId: d.id, successorId: d.targetId, type: "FS", lag: 0 });
    }
  };

  const visibleIds = rows.slice(start, end);
  const visibleSet = useMemo(() => new Set(visibleIds), [visibleIds]);

  // dependencies to draw: both ends visible (with a generous buffer)
  const bufferStart = Math.max(0, start - 30);
  const bufferEnd = Math.min(rows.length, end + 30);
  const bufferSet = useMemo(() => new Set(rows.slice(bufferStart, bufferEnd)), [rows, bufferStart, bufferEnd]);
  const deps = showDependencies ? project.dependencies.filter((d) => bufferSet.has(d.predecessorId) && bufferSet.has(d.successorId)) : [];

  const today = todayISO();
  const todayX = scale.x(toDay(today));
  const statusX = scale.x(toDay(project.statusDate) + 1);
  const totalHeight = rows.length * rowHeight;
  const chartHeight = Math.max(HEADER_H + totalHeight + 120, viewport.viewportHeight);
  const markerHeight = HEADER_H + totalHeight;
  const isSameDate = today === project.statusDate;
  const isColliding = Math.abs(statusX - todayX) < 96;

  return (
    <div className="relative" style={{ width: scale.width, height: chartHeight }}>
      <TimeHeader scale={scale} scrollLeft={viewport.scrollLeft} />
      {isSameDate ? (
        <MergedTodayStatusMarker x={statusX} date={today} height={markerHeight} />
      ) : (
        <>
          <TodayMarker x={todayX} date={today} height={markerHeight} />
          <StatusMarker x={statusX} date={project.statusDate} height={markerHeight} offsetY={isColliding ? 21 : 0} />
        </>
      )}
      <div
        ref={bodyRef}
        className={cn("relative select-none", drag?.mode === "link" && "cursor-crosshair", drag?.mode === "move" && "cursor-grabbing", drag?.mode === "resize" && "cursor-ew-resize")}
        style={{ height: totalHeight, width: scale.width }}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) useProjectStore.getState().select([], null);
        }}
      >
        <Background scale={scale} calendar={calendar} height={totalHeight} />
        {/* row stripes for selection */}
        {visibleIds.map((id, i) => {
          const idx = start + i;
          const sel = selection.includes(id);
          const sched = schedule.tasks.get(id)!;
          return (
            <div
              key={id}
              data-row-id={id}
              className={cn("absolute left-0 w-full border-b border-grid-line", sel ? "bg-tint/8" : sched.isSummary && "bg-muted/30")}
              style={{ top: idx * rowHeight, height: rowHeight }}
              onMouseDown={(e) => {
                e.stopPropagation();
                if (e.metaKey || e.ctrlKey) useProjectStore.getState().toggleSelect(id);
                else useProjectStore.getState().select([id], id);
              }}
            />
          );
        })}

        {/* dependency layer */}
        <svg className="pointer-events-none absolute left-0 top-0" width={scale.width} height={totalHeight}>
          <defs>
            {(["muted", "tint", "critical"] as const).map((k) => (
              <marker key={k} id={`arrow-${k}`} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M0,0.5 L8,4 L0,7.5 z" className={k === "muted" ? "fill-muted-foreground/70" : k === "tint" ? "fill-tint" : "fill-critical"} />
              </marker>
            ))}
          </defs>
          {deps.map((d) => (
            <DepPath
              key={d.id}
              dep={d}
              scale={scale}
              schedule={schedule}
              rowIndex={rowIndex}
              rowHeight={rowHeight}
              showCritical={showCritical}
              selected={selection.includes(d.predecessorId) || selection.includes(d.successorId)}
              emphasis={drivingPath ? (drivingPath.deps.has(d.id) ? "path" : "dim") : "normal"}
            />
          ))}
          {showProgressLine && (
            <ProgressLine rows={visibleIds} firstIndex={start} rowHeight={rowHeight} scale={scale} schedule={schedule} calendar={calendar} statusDate={project.statusDate} />
          )}
          {drag?.mode === "link" && (() => {
            const src = schedule.tasks.get(drag.id)!;
            const i = rowIndex.get(drag.id) ?? 0;
            const x1 = scale.x(toDay(src.finish) + 1);
            const y1 = i * rowHeight + rowHeight / 2;
            return <path d={`M${x1},${y1} L${drag.pointerX},${drag.pointerY}`} className="stroke-tint" strokeWidth={1.5} fill="none" strokeDasharray="4 3" />;
          })()}
        </svg>

        {/* bars */}
        {visibleIds.map((id, i) => {
          const sched = schedule.tasks.get(id)!;
          const task = project.tasks.find((t) => t.id === id)!;
          return (
            <Bar
              key={id}
              id={id}
              name={task.name}
              sched={sched}
              top={(start + i) * rowHeight}
              rowHeight={rowHeight}
              scale={scale}
              baseline={showBaseline && baseline ? baseline.tasks[id] : undefined}
              showCritical={showCritical}
              selected={selection.includes(id)}
              onPath={drivingPath?.tasks.has(id) ?? false}
              dimmed={!!drivingPath && !drivingPath.tasks.has(id) && !sched.isSummary}
              drag={drag?.id === id ? drag : null}
              isLinkTarget={drag?.mode === "link" && drag.targetId === id}
              beginDrag={beginDrag}
              canMove={surface.editSchedule}
              canLink={surface.editWbs}
              label={barLabel}
              showProgressLabel={showProgressLabel}
              showRemainingLabel={showRemainingLabel}
              statusDate={project.statusDate}
              assignee={task.assignee}
              deadline={task.deadline}
              floatDays={showFloat && !sched.isSummary && !sched.isMilestone && sched.totalFloat > 0 ? sched.totalFloat : 0}
              calendar={calendar}
            />
          );
        })}
        {!visibleSet.size && <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">작업이 없습니다</div>}
      </div>
    </div>
  );
}

// ---------------- markers ----------------

const TodayMarker = memo(function TodayMarker({ x, date, height }: { x: number; date: string; height: number }) {
  return (
    <div className="pointer-events-none absolute top-0 z-30" style={{ left: x, height, width: 0 }}>
      <div className="absolute inset-0 z-0">
        <div className="sticky top-7 w-px -translate-x-1/2 bg-muted-foreground/50" style={{ height: Math.max(0, height - 28) }} aria-hidden />
      </div>
      <div className="absolute inset-0 z-10">
        <div className="sticky top-[22px] size-1.5 -translate-x-1/2 rounded-full bg-muted-foreground" aria-hidden />
      </div>
      <div className="absolute inset-0 z-20">
        <div className="sticky top-1 w-max -translate-x-1/2">
          <Tooltip delayDuration={150}>
            <TooltipTrigger asChild>
              <div
                tabIndex={0}
                role="button"
                className="pointer-events-auto flex items-center whitespace-nowrap rounded-full bg-foreground px-2 py-[3px] text-[11px] font-normal leading-none text-background shadow-sm cursor-default hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-opacity"
                aria-label={`오늘: ${formatDate(date, "full")}`}
              >
                오늘 · {formatDate(date, "short")}
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-64 space-y-1">
              <div className="flex items-center gap-1.5 font-normal">
                <span className="size-1.5 rounded-full bg-muted-foreground" aria-hidden />
                <span>오늘 (Today)</span>
                <span className="ml-auto font-mono text-[11px] font-normal opacity-70">{formatDate(date, "numeric")}</span>
              </div>
              <div className="text-caption opacity-85">현재 시스템 날짜입니다.</div>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
});

const StatusMarker = memo(function StatusMarker({
  x,
  date,
  height,
  offsetY = 0,
}: {
  x: number;
  date: string;
  height: number;
  offsetY?: number;
}) {
  const isShifted = offsetY > 0;
  const chipTop = isShifted ? 23 : 4;
  const dotTop = isShifted ? 42 : 22;
  const lineTop = isShifted ? 23 : 28;
  return (
    <div className="pointer-events-none absolute top-0 z-30" style={{ left: x, height, width: 0 }}>
      <div className="absolute inset-0 z-0">
        <div
          className="sticky w-px -translate-x-1/2 bg-tint/70 transition-[top] duration-150"
          style={{ top: lineTop, height: Math.max(0, height - lineTop) }}
          aria-hidden
        />
      </div>
      <div className="absolute inset-0 z-10">
        <div className="sticky size-1.5 -translate-x-1/2 rounded-full bg-tint transition-[top] duration-150" style={{ top: dotTop }} aria-hidden />
      </div>
      <div className="absolute inset-0 z-20">
        <div className="sticky w-max -translate-x-1/2 transition-[top] duration-150" style={{ top: chipTop }}>
          <Tooltip delayDuration={150}>
            <TooltipTrigger asChild>
              <div
                tabIndex={0}
                role="button"
                className="pointer-events-auto flex items-center whitespace-nowrap rounded-full bg-tint px-2 py-[3px] text-[11px] font-normal leading-none text-white shadow-sm cursor-default hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-[filter]"
                aria-label={`진척 기준일: ${formatDate(date, "full")}`}
              >
                기준일 · {formatDate(date, "short")}
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="w-80 max-w-[calc(100vw-1rem)] space-y-1">
              <div className="flex items-center gap-1.5 font-normal">
                <span className="size-1.5 rounded-full bg-tint" aria-hidden />
                <span className="whitespace-nowrap">진척 기준일 (Status Date)</span>
                <span className="ml-auto shrink-0 font-mono text-[11px] font-normal opacity-70">{formatDate(date, "numeric")}</span>
              </div>
              <div className="break-keep text-caption leading-relaxed opacity-85">
                계획과 실제 진척을 비교하는 기준 시점입니다. 선택한 날짜까지의 진행 상황과 지연을 계산합니다.
              </div>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
});

const MergedTodayStatusMarker = memo(function MergedTodayStatusMarker({
  x,
  date,
  height,
}: {
  x: number;
  date: string;
  height: number;
}) {
  return (
    <div className="pointer-events-none absolute top-0 z-30" style={{ left: x, height, width: 0 }}>
      <div className="absolute inset-0 z-0">
        <div className="sticky top-7 w-px -translate-x-1/2 bg-tint/70" style={{ height: Math.max(0, height - 28) }} aria-hidden />
      </div>
      <div className="absolute inset-0 z-10">
        <div className="sticky top-[22px] size-1.5 -translate-x-1/2 rounded-full bg-tint" aria-hidden />
      </div>
      <div className="absolute inset-0 z-20">
        <div className="sticky top-1 w-max -translate-x-1/2">
          <Tooltip delayDuration={150}>
            <TooltipTrigger asChild>
              <div
                tabIndex={0}
                role="button"
                className="pointer-events-auto flex items-center gap-1 whitespace-nowrap rounded-full bg-foreground px-2 py-[3px] text-[11px] font-normal leading-none text-background shadow-sm ring-2 ring-tint ring-offset-1 ring-offset-background cursor-default hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-[filter]"
                aria-label={`오늘 및 진척 기준일: ${formatDate(date, "full")}`}
              >
                <span>오늘</span>
                <span className="opacity-40">·</span>
                <span className="text-tint font-normal">진척 기준일</span>
                <span className="opacity-40">·</span>
                <span>{formatDate(date, "short")}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-64 space-y-1">
              <div className="flex items-center gap-1.5 font-normal">
                <span className="size-1.5 rounded-full bg-tint" aria-hidden />
                <span>오늘 & 진척 기준일</span>
                <span className="ml-auto font-mono text-[11px] font-normal opacity-70">{formatDate(date, "numeric")}</span>
              </div>
              <div className="text-caption opacity-85">
                현재 시스템 날짜이자 플랜의 계획과 실제 진척을 비교하는 시점입니다.
              </div>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
});

const TimeHeader = memo(function TimeHeader({ scale, scrollLeft }: { scale: TimeScale; scrollLeft: number }) {
  return (
    <div className="sticky top-0 z-20 border-b border-border bg-background/95 text-caption text-muted-foreground backdrop-blur" style={{ height: HEADER_H, width: scale.width }}>
      <div className="relative h-[22px] border-b border-grid-line">
        {scale.top.map((t, i) => {
          // keep the label visible while its span is scrolled partially out of view
          const labelX = Math.min(Math.max(t.x, scrollLeft), t.x + Math.max(0, t.width - 72));
          return (
            <div key={i} className="absolute top-0 h-full overflow-hidden border-r border-grid-line" style={{ left: t.x, width: t.width }}>
              <span className="absolute top-0 flex h-full items-center whitespace-nowrap px-2 font-normal text-foreground/80" style={{ left: Math.max(0, labelX - t.x) }}>
                {t.label}
              </span>
            </div>
          );
        })}
      </div>
      <div className="relative h-[22px]">
        {scale.bottom.map((t, i) => (
          <div
            key={i}
            className={cn(
              "absolute top-0 flex h-full items-center overflow-hidden whitespace-nowrap border-r border-grid-line tabular-nums",
              scale.tickMode === "month" ? "px-1.5" : "justify-center",
            )}
            style={{ left: t.x, width: t.width }}
          >
            {t.label}
          </div>
        ))}
      </div>
    </div>
  );
});

// ---------------- background ----------------

const Background = memo(function Background({ scale, calendar, height }: { scale: TimeScale; calendar: { isWorkingDay: (d: number) => boolean }; height: number }) {
  if (scale.tickMode === "month") {
    return (
      <div className="pointer-events-none absolute inset-0">
        {scale.bottom.map((t, i) => (
          <div key={i} className="absolute top-0 h-full border-r border-grid-line" style={{ left: t.x, width: t.width }} />
        ))}
      </div>
    );
  }
  const rects: Array<{ x: number; w: number }> = [];
  let runStart: number | null = null;
  for (let d = scale.startDay; d <= scale.endDay; d++) {
    const nonWorking = d < scale.endDay && !calendar.isWorkingDay(d);
    if (nonWorking && runStart === null) runStart = d;
    if (!nonWorking && runStart !== null) {
      rects.push({ x: scale.x(runStart), w: (d - runStart) * scale.pxPerDay });
      runStart = null;
    }
  }
  return (
    <div className="pointer-events-none absolute inset-0" style={{ height }}>
      {rects.map((r, i) => (
        <div key={i} className="absolute top-0 h-full bg-nonworking" style={{ left: r.x, width: r.w }} />
      ))}
      {scale.bottom.map((t, i) => (
        <div key={i} className="absolute top-0 h-full border-r border-grid-line" style={{ left: t.x, width: t.width }} />
      ))}
    </div>
  );
});

// ---------------- bars ----------------

interface BarProps {
  id: string;
  name: string;
  sched: ScheduledTask;
  top: number;
  rowHeight: number;
  scale: TimeScale;
  baseline?: { start: string; finish: string; duration: number };
  showCritical: boolean;
  selected: boolean;
  /** part of the focused task's driving path */
  onPath: boolean;
  /** another task is focused and this one is not on its driving path */
  dimmed: boolean;
  drag: DragState | null;
  isLinkTarget: boolean;
  beginDrag: (e: React.PointerEvent, mode: DragState["mode"], id: string) => void;
  canMove: boolean;
  canLink: boolean;
  label: BarLabel;
  showProgressLabel: boolean;
  showRemainingLabel: boolean;
  statusDate: string;
  assignee?: string;
  deadline?: string;
  /** total float to draw after the bar (0 = none) */
  floatDays: number;
  calendar: WorkCalendar;
}

const Bar = memo(function Bar({
  id,
  name,
  sched,
  top,
  rowHeight,
  scale,
  baseline,
  showCritical,
  selected,
  onPath,
  dimmed,
  drag,
  isLinkTarget,
  beginDrag,
  canMove,
  canLink,
  label,
  showProgressLabel,
  showRemainingLabel,
  statusDate,
  assignee,
  deadline,
  floatDays,
  calendar,
}: BarProps) {
  const critical = sched.critical && showCritical;
  const move = drag?.mode === "move" ? moveDelta(drag, sched, scale.pxPerDay) : null;
  const dxDays = move ? move.days : drag ? Math.round(drag.dx / scale.pxPerDay) : 0;
  const shift = move ? move.days * scale.pxPerDay : 0;
  const grow = drag?.mode === "resize" ? dxDays * scale.pxPerDay : 0;

  const x1 = scale.x(toDay(sched.start)) + shift;
  const x2 = Math.max(x1 + (sched.isMilestone ? 0 : scale.pxPerDay * 0.6), scale.x(toDay(sched.finish) + 1) + shift + grow);
  const barH = sched.isSummary ? 6 : Math.max(10, Math.min(16, rowHeight - 12));
  const y = top + (rowHeight - barH) / 2;
  const previewFinish = drag && (drag.mode === "move" || drag.mode === "resize") ? fromDay(toDay(sched.finish) + dxDays) : null;
  const done = sched.percentComplete >= 100;

  // label text
  let text: string | null = null;
  if (!sched.isSummary && (scale.tickMode === "day" || scale.tickMode === "week")) {
    if (label === "name") text = name;
    else if (label === "dates") text = sched.isMilestone ? formatDate(sched.start, "short") : formatDateRange(sched.start, sched.finish, "short");
    else if (label === "assignee") text = assignee ?? null;
  }

  // float: from the day after finish through totalFloat working days
  const floatX2 = floatDays > 0 ? scale.x(toDay(calendar.isoOf(sched.ef + floatDays - 1)) + 1) : 0;
  // baseline slip: current finish later than the baseline finish
  const slip = baseline && baseline.finish < sched.finish ? { x1: scale.x(toDay(baseline.finish) + 1), x2: scale.x(toDay(sched.finish) + 1) } : null;
  const deadlineX = deadline ? scale.x(toDay(deadline) + 1) : null;

  const dim = dimmed ? "opacity-35" : "";

  return (
    <>
      {baseline && (
        <div
          className={cn("pointer-events-none absolute rounded-[1px] bg-bar-baseline", dim)}
          style={{ left: scale.x(toDay(baseline.start)), width: Math.max(2, scale.x(toDay(baseline.finish) + 1) - scale.x(toDay(baseline.start))), top: top + rowHeight - 5, height: 3 }}
          title={`기준선 ${formatDateRange(baseline.start, baseline.finish, "short")}`}
        />
      )}
      {slip && slip.x2 - slip.x1 > 1 && (
        <div
          className={cn("pointer-events-none absolute", dim)}
          style={{
            left: slip.x1,
            width: slip.x2 - slip.x1,
            top: top + rowHeight - 5,
            height: 3,
            backgroundImage: `repeating-linear-gradient(135deg, ${critical ? "var(--critical)" : "var(--foreground)"} 0 1px, transparent 1px 3px)`,
            opacity: 0.6,
          }}
          title={`기준선 대비 ${formatDate(baseline!.finish, "short")} → ${formatDate(sched.finish, "short")}`}
        />
      )}
      {floatDays > 0 && floatX2 > x2 && (
        <div
          className={cn("pointer-events-none absolute rounded-r-[2px] bg-bar/45", dim)}
          style={{ left: x2, width: floatX2 - x2, top: y + barH / 2 - 1, height: 2 }}
          title={`여유 ${floatDays}d`}
        />
      )}
      {sched.isSummary ? (
        <div className="pointer-events-none absolute" style={{ left: x1, width: x2 - x1, top: y, height: barH }}>
          <div className={cn("absolute inset-x-0 top-0 h-[5px] rounded-[1px] bg-bar-summary", onPath && "shadow-[0_0_0_1px_var(--tint)]")} />
          <div className={cn("absolute left-0 top-0 h-[9px] w-[2px]", critical ? "bg-critical" : "bg-bar-summary")} />
          <div className="absolute right-0 top-0 h-[9px] w-[2px] bg-bar-summary" />
        </div>
      ) : sched.isMilestone ? (
        <div
          data-row-id={id}
          className={cn("group absolute", canMove ? "cursor-grab" : "cursor-default", dim, isLinkTarget && "ring-2 ring-tint rounded-sm")}
          style={{ left: x1 - 7, top: top + rowHeight / 2 - 7, width: 14, height: 14 }}
          onPointerDown={canMove ? (e) => beginDrag(e, "move", id) : undefined}
          title={`${name} · ${formatDate(sched.start, "short")}${done ? " · 완료" : ""}`}
        >
          <div
            className={cn(
              "absolute inset-[2px] rotate-45 rounded-[1px]",
              done ? "bg-foreground" : "bg-card",
              critical ? "border-[1.5px] border-critical" : done ? "" : "border-[1.5px] border-foreground/70",
              selected && "ring-2 ring-tint ring-offset-1 ring-offset-card",
              onPath && !selected && "ring-1 ring-tint",
            )}
          />
          {canLink && <LinkHandle x={12} onPointerDown={(e) => beginDrag(e, "link", id)} />}
        </div>
      ) : (
        <div
          data-row-id={id}
          className={cn(
            "group absolute rounded-[1px] bg-bar",
            canMove ? "cursor-grab" : "cursor-default",
            dim,
            selected && "ring-2 ring-tint ring-offset-1 ring-offset-card",
            onPath && !selected && "ring-1 ring-tint",
            isLinkTarget && "ring-2 ring-tint",
          )}
          style={{ left: x1, width: x2 - x1, top: y, height: barH }}
          onPointerDown={canMove ? (e) => beginDrag(e, "move", id) : undefined}
          title={`${name}\n${formatDateRange(sched.start, sched.finish, "short")} · ${sched.duration}d · 여유 ${sched.totalFloat}d · ${sched.percentComplete}%`}
        >
          <div className="absolute inset-y-0 left-0 bg-foreground/75" style={{ width: `${Math.min(100, sched.percentComplete)}%` }} />
          {critical && <div className="absolute inset-y-0 left-0 z-[1] w-0.5 bg-critical" />}
          {showProgressLabel && fitsProgressLabel(x2 - x1, barH) && <BarProgressLabel percent={sched.percentComplete} critical={critical} />}
          {canMove && <div className="absolute inset-y-0 right-0 w-2 cursor-ew-resize" onPointerDown={(e) => beginDrag(e, "resize", id)} />}
          {canLink && <LinkHandle x={x2 - x1 + 4} onPointerDown={(e) => beginDrag(e, "link", id)} />}
        </div>
      )}
      {sched.isSummary && showRemainingLabel && (
        <RemainingLabel
          info={remainingInfo(sched, statusDate, calendar)}
          className="absolute flex -translate-x-full items-center pr-1"
          style={{ left: x1, top, height: rowHeight }}
        />
      )}
      {deadlineX !== null && (
        <div
          className={cn("pointer-events-none absolute", sched.deadlineMissed ? "text-critical" : "text-foreground/70")}
          style={{ left: deadlineX - 5, top: top + 1, width: 10, height: 6 }}
          title={`마감 ${formatDate(deadline, "short")}${sched.deadlineMissed ? " · 초과" : ""}`}
        >
          <svg viewBox="0 0 10 6" width={10} height={6}>
            <path d="M0,0 H10 L5,6 Z" fill="currentColor" />
          </svg>
        </div>
      )}
      {move?.clamped && drag?.minDay !== undefined && (
        <>
          <div className="pointer-events-none absolute w-px bg-tint" style={{ left: scale.x(drag.minDay), top, height: rowHeight }} />
          <div className="pointer-events-none absolute z-30 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-caption text-background shadow-md" style={{ left: scale.x(drag.minDay), top: top - 22 }}>
            {drag.minReason}
          </div>
        </>
      )}
      {(text || previewFinish) && (
        <div className="pointer-events-none absolute flex items-center whitespace-nowrap text-muted-foreground" style={{ left: Math.max(x2, floatX2) + 14, top, height: rowHeight }}>
          {previewFinish ? (
            <span className="text-tint">
              {formatDate(fromDay(toDay(sched.start) + (move ? move.days : 0)), "short")} → {formatDate(previewFinish, "short")}
            </span>
          ) : (
            text
          )}
        </div>
      )}
    </>
  );
});

function LinkHandle({ x, onPointerDown }: { x: number; onPointerDown: (e: React.PointerEvent) => void }) {
  return (
    <div
      className="absolute top-1/2 hidden size-[9px] -translate-y-1/2 cursor-crosshair rounded-full border border-tint bg-card group-hover:block"
      style={{ left: x }}
      onPointerDown={onPointerDown}
      title="드래그하여 후행 작업 연결 (FS)"
    />
  );
}

// ---------------- dependency path ----------------

const DepPath = memo(function DepPath({
  dep,
  scale,
  schedule,
  rowIndex,
  rowHeight,
  showCritical,
  selected,
  emphasis,
}: {
  dep: Dependency;
  scale: TimeScale;
  schedule: { tasks: Map<string, ScheduledTask> };
  rowIndex: Map<string, number>;
  rowHeight: number;
  showCritical: boolean;
  selected: boolean;
  emphasis: "normal" | "path" | "dim";
}) {
  const p = schedule.tasks.get(dep.predecessorId);
  const s = schedule.tasks.get(dep.successorId);
  const pi = rowIndex.get(dep.predecessorId);
  const si = rowIndex.get(dep.successorId);
  if (!p || !s || pi === undefined || si === undefined) return null;
  const fromEnd = dep.type === "FS" || dep.type === "FF";
  const toStart = dep.type === "FS" || dep.type === "SS";
  const px = p.isMilestone ? scale.x(toDay(p.start)) + (fromEnd ? 7 : -7) : fromEnd ? scale.x(toDay(p.finish) + 1) : scale.x(toDay(p.start));
  const sx = s.isMilestone ? scale.x(toDay(s.start)) + (toStart ? -7 : 7) : toStart ? scale.x(toDay(s.start)) : scale.x(toDay(s.finish) + 1);
  const py = pi * rowHeight + rowHeight / 2;
  const sy = si * rowHeight + rowHeight / 2;
  const dir = fromEnd ? 1 : -1;
  const into = toStart ? -1 : 1; // arrow approaches from the left when entering a start
  const stub = 10;
  let d: string;
  const x0 = px + dir * stub;
  const xEnd = sx + into * stub;
  if ((toStart && xEnd >= x0) || (!toStart && xEnd <= x0)) {
    // simple: out, vertical, in
    const midY = sy;
    d = `M${px},${py} H${x0} V${midY} H${xEnd} H${sx}`;
  } else {
    // route around: out, half-row down/up, back, down/up to row, in
    const yMid = py + (sy > py ? rowHeight / 2 : -rowHeight / 2);
    d = `M${px},${py} H${x0} V${yMid} H${xEnd} V${sy} H${sx}`;
  }
  const critical = showCritical && p.critical && s.critical;
  const path = emphasis === "path";
  const dim = emphasis === "dim";
  const color = path ? "tint" : critical && !dim ? "critical" : selected && !dim ? "tint" : "muted";
  return (
    <path
      d={d}
      fill="none"
      className={cn(
        color === "critical" ? "stroke-critical" : color === "tint" ? "stroke-tint" : "stroke-muted-foreground/55",
        dim && "opacity-30",
      )}
      strokeWidth={path ? 2 : selected || critical ? 1.5 : 1}
      markerEnd={`url(#arrow-${color})`}
    />
  );
});

// ---------------- progress line ----------------

/**
 * MS Project-style progress line: a vertical line at the status date that bends left to each
 * unfinished task's progress point (behind) or right (ahead). Tasks not yet due stay on the line.
 */
const ProgressLine = memo(function ProgressLine({
  rows,
  firstIndex,
  rowHeight,
  scale,
  schedule,
  calendar,
  statusDate,
}: {
  rows: string[];
  firstIndex: number;
  rowHeight: number;
  scale: TimeScale;
  schedule: { tasks: Map<string, ScheduledTask> };
  calendar: WorkCalendar;
  statusDate: string;
}) {
  const statusX = scale.x(toDay(statusDate) + 1);
  const pts: string[] = [];
  const dots: Array<{ x: number; y: number }> = [];
  rows.forEach((id, i) => {
    const t = schedule.tasks.get(id);
    if (!t) return;
    const idx = firstIndex + i;
    const yTop = idx * rowHeight;
    const yMid = yTop + rowHeight / 2;
    let x = statusX;
    if (!t.isSummary && !t.isMilestone && t.start <= statusDate && t.percentComplete < 100) {
      const doneOrd = t.es + Math.round((t.percentComplete / 100) * t.duration);
      x = scale.x(toDay(calendar.isoOf(doneOrd)));
      if (Math.abs(x - statusX) > 1) dots.push({ x, y: yMid });
      else x = statusX;
    }
    pts.push(`${statusX},${yTop}`, `${x},${yMid}`, `${statusX},${yTop + rowHeight}`);
  });
  if (!dots.length) return null;
  // drawn in the status-date colour (dashed) so it reads as "where the status line bends", not as a link
  return (
    <g className="text-tint">
      <polyline points={pts.join(" ")} fill="none" stroke="currentColor" strokeOpacity={0.6} strokeWidth={1} strokeDasharray="3 2" strokeLinejoin="round" />
      {dots.map((d, i) => (
        <circle key={i} cx={d.x} cy={d.y} r={2.5} fill="currentColor" />
      ))}
    </g>
  );
});
